import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { Settings, Check, X } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MODULE_ICONS = {
  leave_management: 'Calendar', classes: 'GraduationCap', students: 'BookOpen',
  fees: 'IndianRupee', exams: 'ClipboardList', tickets: 'MessageSquare',
  analytics: 'BarChart', announcements: 'Megaphone', holidays: 'Calendar',
  work_schedule: 'Clock', billing: 'CreditCard', documents: 'FileText',
};

export default function ModuleConfig() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [modules, setModules] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchModules = async () => {
      try {
        const res = await axios.get(`${API}/school/modules`, { headers });
        setModules(res.data.data.modules || {});
      } catch { toast.error('Failed to load module config'); }
      setLoading(false);
    };
    fetchModules();
  }, []);

  const toggle = (key) => {
    setModules(prev => ({
      ...prev,
      [key]: { ...prev[key], enabled: !prev[key].enabled },
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/school/modules`, { modules }, { headers });
      toast.success('Module configuration saved. Refresh to see changes in sidebar.');
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  if (loading) return <div className="flex items-center justify-center py-20 text-slate-400">Loading...</div>;

  return (
    <div className="space-y-6" data-testid="module-config-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="module-config-title">Module Configuration</h1>
          <p className="text-sm text-slate-500 mt-1">Enable or disable ERP modules for your school</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-teal-500 hover:bg-teal-600 text-white" data-testid="save-modules-btn">
          {saving ? 'Saving...' : 'Save Configuration'}
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {Object.entries(modules).map(([key, mod]) => (
          <div key={key}
            className={`bg-white rounded-xl border-2 p-5 cursor-pointer transition-all ${mod.enabled ? 'border-teal-200 shadow-sm' : 'border-slate-100 opacity-60'}`}
            onClick={() => toggle(key)}
            data-testid={`module-toggle-${key}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${mod.enabled ? 'bg-teal-50' : 'bg-slate-50'}`}>
                  <Settings className={`h-5 w-5 ${mod.enabled ? 'text-teal-600' : 'text-slate-400'}`} />
                </div>
                <div>
                  <p className="font-semibold text-slate-900">{mod.label}</p>
                  <p className="text-xs text-slate-400 capitalize">{key.replace(/_/g, ' ')}</p>
                </div>
              </div>
              <div className={`h-6 w-6 rounded-full flex items-center justify-center ${mod.enabled ? 'bg-teal-500' : 'bg-slate-200'}`}>
                {mod.enabled ? <Check className="h-3.5 w-3.5 text-white" /> : <X className="h-3.5 w-3.5 text-slate-400" />}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
